/**
 * O álbum de vocês: a foto como o app a guarda.
 *
 * É o `MemoryPhoto` de `lib/features/gallery/domain/models/memory_photo.dart`
 * com as mesmas chaves — os bytes moram no armazenamento de arquivos e só a
 * legenda, a data e as etiquetas passam pelo armazém, exatamente como lá.
 */

import { gravarData, lerData } from '../nucleo/tempo';

export interface Foto {
  id: string;
  /** O endereço da imagem já hospedada. Sem ele o cartão não tem o que mostrar. */
  endereco: string;
  legenda: string;
  quem: string;
  quando: Date;
  etiquetas: string[];
}

/** O `MemoryPhoto.fromMap`: o que falta vira vazio, e a data vira agora. */
export function fotoDeMapa(id: string, dados: Record<string, unknown>): Foto {
  const etiquetas = Array.isArray(dados.tags) ? dados.tags.map((t) => String(t)) : [];
  return {
    id,
    endereco: typeof dados.imageUrl === 'string' ? dados.imageUrl : '',
    legenda: typeof dados.caption === 'string' ? dados.caption : '',
    quem: typeof dados.uploadedBy === 'string' ? dados.uploadedBy : '',
    quando: lerData(dados.uploadedAt) ?? new Date(),
    etiquetas,
  };
}

/** O `MemoryPhoto.toMap`, com os nomes que o Flutter gravou. */
export function fotoParaMapa(foto: Omit<Foto, 'id'>): Record<string, unknown> {
  return {
    imageUrl: foto.endereco,
    caption: foto.legenda,
    uploadedBy: foto.quem,
    uploadedAt: gravarData(foto.quando),
    tags: foto.etiquetas,
  };
}

/**
 * Da mais nova para a mais velha, cortando no que a tela desenha de uma vez.
 * É o `getPhotosStream`: o corte é da tela, não do banco.
 */
export function fotosRecentes(fotos: Foto[], limite = 40): Foto[] {
  const lista = [...fotos].sort((a, b) => b.quando.getTime() - a.quando.getTime());
  return lista.length <= limite ? lista : lista.slice(0, limite);
}

/**
 * Busca por legenda ou etiqueta, no álbum inteiro e sem ligar para maiúsculas.
 * O `searchPhotos` não corta em 40: quem procura quer achar.
 */
export function buscarFotos(fotos: Foto[], procura: string): Foto[] {
  const busca = procura.trim().toLowerCase();
  if (!busca) return fotosRecentes(fotos);
  const achadas = fotos.filter(
    (f) =>
      f.legenda.toLowerCase().includes(busca) ||
      f.etiquetas.some((t) => t.toLowerCase().includes(busca)),
  );
  return achadas.sort((a, b) => b.quando.getTime() - a.quando.getTime());
}

/** "Neste dia": mesmo dia e mês, de outros anos. O `getPhotosFromThisDay`. */
export function fotosDesteDia(fotos: Foto[], hoje = new Date()): Foto[] {
  return fotos.filter(
    (f) =>
      f.quando.getMonth() === hoje.getMonth() &&
      f.quando.getDate() === hoje.getDate() &&
      f.quando.getFullYear() !== hoje.getFullYear(),
  );
}
